import React, { useState, useEffect } from "react";
import InputWithLabel from "./InputWithLabel";
import Button from "../buttons/button";
import SelectBox from "./SelectBox ";
import { useGetWorkingAreasQuery } from "../../../app/globalApi";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { capitalize } from "../../../helpers/Utils";
import useTranslator from "../../../hooks/useTranslator";

// Define default date range for "This Week"
const thisWeekStart = moment().startOf("isoWeek").format("YYYY-MM-DD");
const thisWeekEnd = moment().endOf("isoWeek").format("YYYY-MM-DD");

const initialFormData = {
  employee_name: "",
  date_of_joining: "",
  employee_role_id: "",
  attendance_start_date: thisWeekStart, // Set default start date
  attendance_end_date: thisWeekEnd, // Set default end date
};


const today = moment().format("YYYY-MM-DD");

const StaffAttendanceReportFilter = ({
  onApplyFilter,
  date,
  onClose,
  filters = {},
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  let activeTab = searchParams.get("activeTab") ?? 8;
  const [filtersData, setFiltersData] = useState(initialFormData);
  const [roleList, setRoleList] = useState([]);
  const [selectedOption, setSelectedOption] = useState("This Week"); // Set default selection to "This Week"
  const { data: workingAreas } = useGetWorkingAreasQuery();
  const { _t } = useTranslator();
  
  const dateRanges = [
    {
      label: _t("date_range_yesterday", "Yesterday"),
      value: "Yesterday",
    },
    {
      label: _t("date_range_today", "Today"),
      value: "Today",
    },
    {
      label: _t("date_range_this_week", "This Week"),
      value: "This Week",
    },
    {
      label: _t("date_range_this_month", "This Month"),
      value: "This Month",
    },
    {
      label: _t("date_range_custom", "Custom"),
      value: "Custom",
    },
  ];

  useEffect(() => {
    if (workingAreas !== "") {
      if (activeTab) {
        const selectedArea = workingAreas?.data?.find(
          (area) => area.id === parseInt(activeTab)
        );
        const filteredRoles = selectedArea ? selectedArea.roles : [];
        setRoleList(filteredRoles);
      } else {
        setRoleList([]);
      }
    }
  }, [workingAreas, activeTab]);

  useEffect(() => {
    // Check if filters have date ranges defined
    if (filters.attendance_start_date && filters.attendance_end_date) {
      // Attempt to detect which date range the filters match
      const start = filters.attendance_start_date;
      const end = filters.attendance_end_date;

      // Try to determine which preset option the dates correspond to
      if (start === end && start === moment().format("YYYY-MM-DD")) {
        setSelectedOption("Today");
      } else if (start === end && start === moment().subtract(1, "days").format("YYYY-MM-DD")) {
        setSelectedOption("Yesterday");
      } else if (
        start === moment().startOf("isoWeek").format("YYYY-MM-DD") &&
        end === moment().endOf("isoWeek").format("YYYY-MM-DD")
      ) {
        setSelectedOption("This Week");
      } else if (
        start === moment().startOf("month").format("YYYY-MM-DD") &&
        end === moment().endOf("month").format("YYYY-MM-DD")
      ) {
        setSelectedOption("This Month");
      } else {
        setSelectedOption("Custom");
      }
    } else if (!filters.attendance_start_date && !filters.attendance_end_date) {
      // If no dates provided, default to "This Week"
      setSelectedOption("This Week");
      setFiltersData(prev => ({
        ...prev,
        attendance_start_date: thisWeekStart,
        attendance_end_date: thisWeekEnd
      }));
    }

    // Update rest of filter data
    setFiltersData((prev) => ({
      ...prev,
      ...filters,
    }));
  }, [filters]);

  // Initialize with "This Week" dates when component mounts
  useEffect(() => {
    // Only set default dates if none were provided in filters
    if (!filters.attendance_start_date && !filters.attendance_end_date) {
      // Apply initial filter with "This Week" date range
      onApplyFilter({
        ...filtersData,
        attendance_start_date: thisWeekStart,
        attendance_end_date: thisWeekEnd,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFiltersData({ ...filtersData, [e.target.name]: e.target.value });
  };

  const handleReset = (e) => {
    e.preventDefault();
    // Reset to "This Week" instead of empty values
    const resetData = {
      ...initialFormData,
      attendance_start_date: thisWeekStart,
      attendance_end_date: thisWeekEnd,
    };
    setFiltersData(resetData);
    setSelectedOption("This Week");
    onApplyFilter(resetData);
    if (onClose) {
      onClose();
    }
  };

  const handleApply = (e) => {
    e.preventDefault();
    onApplyFilter({ ...filtersData });
    if (onClose) {
      onClose();
    }
  };

  const handleOptionChange = (e) => {
    const value = e.target.value;
    setSelectedOption(value);

    let start, end;

    switch (value) {
      case "Yesterday":
        start = moment().subtract(1, "days").format("YYYY-MM-DD");
        end = start;
        break;

      case "Today":
        start = moment().format("YYYY-MM-DD");
        end = start;
        break;

      case "This Week":
        start = moment().startOf("isoWeek").format("YYYY-MM-DD"); // Monday start
        end = moment().endOf("isoWeek").format("YYYY-MM-DD"); // Sunday end
        break;

      case "This Month":
        start = moment().startOf("month").format("YYYY-MM-DD");
        end = moment().endOf("month").format("YYYY-MM-DD");
        break;

      case "Custom":
        start = "";
        end = "";
        break;

      default:
        start = "";
        end = "";
    }

    setFiltersData((prev) => ({
      ...prev,
      attendance_start_date: start,
      attendance_end_date: end,
    }));
  };

  return (
    <div className="dropdown_body">
      <h5 className="mb-3 border-bottom">{_t("filter_heading", "Filter")}</h5>

      <InputWithLabel
        label={_t("employee_name", "Employee Name")}
        type="text"
        name="employee_name"
        value={filtersData.employee_name}
        onChange={handleChange}
        placeholder="Employee Name..."
      />
      <InputWithLabel
        label={_t("date_of_joining", "Date of Join")}
        type="date"
        name="date_of_joining"
        value={filtersData.date_of_joining}
        onChange={handleChange}
        max={today}
      />
      <SelectBox
        label={_t("select_date_range", "Select Date Range")}
        name="date_range"
        value={selectedOption}
        onChange={handleOptionChange}
        options={dateRanges?.map((list) => ({
          value: list.value,
          label: _t(list.label, list.label),
        }))}
        disabled={activeTab === ""}
      />
      {selectedOption === "Custom" && (
        <>
          <InputWithLabel
            label={_t("start_date", "Start Date")}
            type="date"
            name="attendance_start_date"
            value={filtersData.attendance_start_date}
            onChange={handleChange}
            max={filtersData.attendance_end_date}
          />
          <InputWithLabel
            label={_t("end_date", "End Date")}
            type="date"
            name="attendance_end_date"
            value={filtersData.attendance_end_date}
            onChange={handleChange}
            min={filtersData.attendance_start_date}
          />
        </>
      )}
      <SelectBox
        label={_t("employee_role", "Role")}
        name="employee_role_id"
        value={filtersData.employee_role_id}
        onChange={handleChange}
        options={roleList?.map((list) => ({
          value: list.id,
          label: capitalize(list.name),
        }))}
        disabled={activeTab === ""}
      />
      <div className="d-flex justify-content-between mt-3">
        <Button
          onClick={handleReset}
          label={_t("reset_button", "Reset")}
          className="reset_btn"
        />
        <Button
          className="cmn_btn"
          label={_t("apply_button", "Apply")}
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default StaffAttendanceReportFilter;